(() => {
    const SELECTOR = "[data-parallax], [data-parallax-start], .line-parallax";
    const WATCH = `${SELECTOR}, .animate`;

    const items = new Map();
    let list = [];
    let viewportHeight = 0;
    let viewportWidth = 0;
    let lenis = null;
    let refreshQueued = false;

    const getAbsoluteTop = (element) => {
        let top = 0;
        let current = element;
        while (current) {
            top += current.offsetTop;
            current = current.offsetParent;
        }
        return top;
    };

    const parseAlign = (val) => {
        if (val === "top") return 0;
        if (val === "center") return 0.5;
        if (val === "bottom") return 1;
        if (val && val.includes("%")) return parseFloat(val) / 100;
        return 0;
    };

    const getScrollPoint = (triggerString, elTop, elHeight, vHeight) => {
        const [elVal = "top", viewVal = "bottom"] = triggerString.split(" ").map(s => s.trim());
        return (elTop + elHeight * parseAlign(elVal)) - vHeight * parseAlign(viewVal);
    };

    const parseValue = (str, defaultUnit = "%") => {
        if (!str) return { val: 0, unit: defaultUnit };
        const match = str.trim().match(/([-\d.]+)(.*)/);
        const val = match ? parseFloat(match[1]) : NaN;
        if (Number.isNaN(val)) return { val: 0, unit: defaultUnit };
        return { val, unit: match[2].trim() || defaultUnit };
    };

    const measure = (el, data) => {
        const isLine = el.classList.contains("line-parallax");
        const elementTop = getAbsoluteTop(el);
        const elementHeight = el.offsetHeight || (isLine ? 1 : 0);

        data.isLine = isLine;
        data.type = el.getAttribute("data-parallax-type") || "vertical";

        if (isLine) {
            data.startTarget = 0;
            data.endTarget = 1;
            data.unit = "";
            data.scrollStart = getScrollPoint("top 80%", elementTop, elementHeight, viewportHeight);
            data.scrollEnd = data.scrollStart + elementHeight;
        } else {
            let startRaw = el.getAttribute("data-parallax-start");
            let endRaw = el.getAttribute("data-parallax-end");

            if (!startRaw && !endRaw) {
                const parts = (el.getAttribute("data-parallax") || "0,0").split(",");
                [startRaw = "0", endRaw = parts[0]] = parts;
            } else {
                startRaw = startRaw || "0";
                endRaw = endRaw || "0";
            }

            const defaultUnit = (data.type === "opacity" || data.type === "scale") ? "" : "%";
            const s = parseValue(startRaw, defaultUnit);
            const e = parseValue(endRaw, s.unit);

            data.startTarget = s.val;
            data.endTarget = e.val;
            data.unit = s.unit;

            const triggers = (el.getAttribute("data-parallax-trigger") || "top bottom, bottom top").split(",");
            const startTrigger = triggers[0].trim();
            const endTrigger = triggers[1] ? triggers[1].trim() : "bottom top";

            data.scrollStart = getScrollPoint(startTrigger, elementTop, elementHeight, viewportHeight);
            data.scrollEnd = getScrollPoint(endTrigger, elementTop, elementHeight, viewportHeight);
        }

        data.range = (data.scrollEnd - data.scrollStart) || 1;
        data.distance = data.endTarget - data.startTarget;
        data.lastP = null;
        data.lastVal = null;
    };

    const write = (el, data, val) => {
        if (data.isLine) {
            el.style.transform = `scaleY(${val})`;
            return;
        }

        switch (data.type) {
            case "horizontal":
                el.style.transform = `translate3d(${val}${data.unit}, 0, 0)`;
                break;
            case "scale":
                el.style.transform = `scale(${val})`;
                break;
            case "opacity":
                el.style.opacity = val;
                break;
            default:
                el.style.transform = `translate3d(0, ${val}${data.unit}, 0)`;
        }
    };

    const update = () => {
        if (!lenis && window.lenis) attachLenis();
        const y = lenis ? lenis.scroll : window.scrollY;

        for (const d of list) {
            let p = (y - d.scrollStart) / d.range;
            p = p < 0 ? 0 : (p > 1 ? 1 : p);

            if (p === d.lastP) continue;
            d.lastP = p;

            const val = Math.round((d.startTarget + d.distance * p) * 10000) / 10000;

            if (val === d.lastVal) continue;
            d.lastVal = val;

            write(d.el, d, val);
        }
    };

    const attachLenis = () => {
        lenis = window.lenis;
        window.removeEventListener("scroll", update);
        lenis.on("scroll", update);
    };

    const animateObserver = new IntersectionObserver((entries) => {
        for (const entry of entries)
            entry.target.classList.toggle("visible", entry.intersectionRatio >= 0.7);
    }, { threshold: [0.7] });

    const refresh = () => {
        refreshQueued = false;

        for (const [el] of items)
            if (!el.isConnected) items.delete(el);

        for (const el of document.querySelectorAll(SELECTOR))
            if (!items.has(el)) items.set(el, { el });

        for (const el of document.querySelectorAll(".animate:not(.js-obs-animate)")) {
            el.classList.add("js-obs-animate");
            animateObserver.observe(el);
        }

        const width = document.documentElement.clientWidth;
        const height = document.documentElement.clientHeight;
        const touch = window.matchMedia("(pointer: coarse)").matches;

        if (!viewportHeight || !touch || width !== viewportWidth) {
            viewportHeight = height;
        }
        viewportWidth = width;

        list = Array.from(items.values());
        for (const data of list)
            measure(data.el, data);

        update();
    };

    const queueRefresh = () => {
        if (refreshQueued) return;
        refreshQueued = true;
        requestAnimationFrame(refresh);
    };

    const isRelevant = (node) =>
        node.nodeType === 1 && (node.matches(WATCH) || node.querySelector(WATCH) !== null);

    const onMutations = (records) => {
        for (const record of records) {
            for (const node of record.addedNodes)
                if (isRelevant(node)) return queueRefresh();

            for (const node of record.removedNodes)
                if (isRelevant(node)) return queueRefresh();
        }
    };

    const init = () => {
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", queueRefresh);
        window.addEventListener("load", queueRefresh);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueRefresh);

        new ResizeObserver(queueRefresh).observe(document.body);
        new MutationObserver(onMutations).observe(document.body, { childList: true, subtree: true });

        refresh();
    };

    if (document.readyState === "loading")
        document.addEventListener("DOMContentLoaded", init);
    else
        init();
})();